import { useId, useState, type FormEvent } from "react";
import { useLocation } from "wouter";
import { useCreateProject } from "../../api/queries";
import { Button } from "../../components/Button";
import { Modal } from "../../components/Modal";
import { useToast } from "../../toast/ToastContext";
import "../../components/Form.css";

interface CreateProjectModalProps {
  open: boolean;
  fleetId: string;
  onClose: () => void;
}

export function CreateProjectModal({ open, fleetId, onClose }: CreateProjectModalProps) {
  const nameId = useId();
  const descriptionId = useId();
  const { pushToast } = useToast();
  const [, navigate] = useLocation();
  const mutation = useCreateProject(fleetId);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  function handleClose() {
    setName("");
    setDescription("");
    mutation.reset();
    onClose();
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const submittedName = name.trim();
    mutation.mutate(
      { name: submittedName, description: description.trim() || undefined },
      {
        onSuccess: (created) => {
          pushToast({ title: `Project "${created.name}" created.`, tone: "success" });
          handleClose();
          navigate(`/projects/${created.id}`);
        },
        // Deliberately no toast on error — the inline form-error below stays
        // legible while the operator fixes a name conflict, matching
        // DeployStackModal's precedent.
      },
    );
  }

  const canSubmit = name.trim().length > 0 && !mutation.isPending;

  return (
    <Modal open={open} title="New project" onClose={handleClose} initialFocusId={nameId}>
      <form onSubmit={handleSubmit} noValidate>
        {mutation.isError && (
          <p className="form-error" role="alert">
            {mutation.error instanceof Error ? mutation.error.message : "Couldn't create this project."}
          </p>
        )}

        <label className="form-field" htmlFor={nameId}>
          <span>Name</span>
          <input
            id={nameId}
            type="text"
            autoComplete="off"
            spellCheck={false}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            maxLength={63}
          />
        </label>

        <label className="form-field" htmlFor={descriptionId}>
          <span>Description (optional)</span>
          <input
            id={descriptionId}
            type="text"
            autoComplete="off"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={200}
          />
        </label>

        <div className="form-actions">
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={!canSubmit}>
            {mutation.isPending ? "Creating…" : "Create project"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
